import {
  Backdrop,
  Box,
  Chip,
  Modal,
  Slide,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppCustomStyles } from "../../../../assets/theme/custom/hooks";
import { AppButton } from "../../../../components/form";
import type { PrepChainPreviewResponse } from "../../../../types/prep";

type Props = {
  open: boolean;
  preview: PrepChainPreviewResponse | null;
  onClose: () => void;
};

function formatCell(value: unknown): string {
  if (value == null) return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

/** Bottom sheet using Modal — avoids global MuiDrawer sidenav theme overrides. */
export default function PrepChainPreviewDrawer({ open, preview, onClose }: Props) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const rows = preview?.sampleRows ?? [];
  const columns =
    rows.length > 0
      ? Object.keys(rows[0])
      : (preview?.cumulativeFields ?? []).map((f) => f.path);

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{ backdrop: { timeout: 250, sx: custom.components.dataPrep.chainPreviewBackdrop } }}
    >
      <Slide direction="up" in={open} mountOnEnter unmountOnExit timeout={250}>
        <Box
          role="dialog"
          aria-modal="true"
          sx={custom.components.dataPrep.chainPreviewDrawer}
        >
          <Box p={2}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h6">{t("prep.preview.title")}</Typography>
              <AppButton variant="outlined" size="small" onClick={onClose}>
                {t("common.close")}
              </AppButton>
            </Stack>
            {preview && (
              <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  label={`${t("prep.preview.rowCount")}: ${preview.sampleRowCount}`}
                  color="primary"
                />
                {preview.expansionSummary && (
                  <Chip size="small" label={preview.expansionSummary} variant="outlined" />
                )}
              </Stack>
            )}
            {preview?.errors?.length ? (
              <Typography color="error" variant="body2">
                {preview.errors.join("; ")}
              </Typography>
            ) : columns.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t("prep.preview.noRows")}
              </Typography>
            ) : (
              <Box sx={custom.pages.shared.overflowAuto}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {columns.map((col) => (
                        <TableCell key={col}>{col}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, idx) => (
                      <TableRow key={idx}>
                        {columns.map((col) => (
                          <TableCell key={col} sx={custom.pages.shared.tableCellEllipsis}>
                            {formatCell(row[col])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
            {preview?.cumulativeFields && preview.cumulativeFields.length > 0 && (
              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>
                  {t("prep.preview.cumulativeFields")}
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {preview.cumulativeFields.map((f) => (
                    <Chip key={f.path} size="small" label={f.path} variant="outlined" />
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        </Box>
      </Slide>
    </Modal>
  );
}
